import { CFormCheck, CSpinner, CTableDataCell, CTableRow } from "@coreui/react";
import {
  ArrowTopRightOnSquareIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/24/solid";
import { useState } from "react";
import { Link } from "react-router-dom";
import DeleteIcon from "../../../components/icons/DeleteIcon";
import EditIcon from "../../../components/icons/EditIcon";
import TranslateIcon from "../../../components/icons/TranslateIcon";

import { colors } from "../../../config/theme";
import { useAuth } from "../../../hooks/useAuth";
import { useDeletePlace, useRowSelection } from "./hooks";
import PlaceRowModals from "./PlaceRowModals";
import { getPlaceImage } from "./placeUtils";
import type { ActiveModal, PlaceRowProps } from "../../../helpers/types";
import { tdStyle } from "../../../helpers/constants";
import { Chip, Dash, IconAction } from "../../../helpers/general";

function PlaceNameCell({ place }: { place: Place }) {
  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: 220 }}>
      <img
        src={getPlaceImage(place)}
        alt=""
        width={44}
        height={44}
        className="flex-shrink-0"
        style={{ borderRadius: 10, objectFit: "cover" }}
      />
      <div style={{ minWidth: 0 }}>
        <Link
          to={`/places/${place?.id}`}
          className="fw-semibold text-capitalize d-block text-truncate"
          style={{ maxWidth: 260 }}
          title={place?.name}
        >
          {place?.name}
        </Link>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {place?.city?.label}
        </div>
      </div>
    </div>
  );
}

export default function PlaceRow({ place, deletedList }: PlaceRowProps) {
  const { isAdmin } = useAuth();
  const { isChecked, toggle } = useRowSelection(place.id, deletedList);
  const { remove, isDeleting } = useDeletePlace();
  const [active, setActive] = useState<ActiveModal>(null);

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      {isAdmin && (
        <CTableDataCell style={{ ...tdStyle, width: 44 }}>
          <CFormCheck
            checked={isChecked}
            onChange={(e) => toggle(e.target.checked)}
          />
        </CTableDataCell>
      )}

      <CTableDataCell style={tdStyle}>
        <PlaceNameCell place={place} />
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        {place?.isVisible ? (
          <Chip tone="success">Visible</Chip>
        ) : (
          <Chip tone="danger">Masqué</Chip>
        )}
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        {place?.phone ? (
          <span style={{ whiteSpace: "nowrap" }}>{place.phone}</span>
        ) : (
          <Dash />
        )}
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        {place?.category?.label ? <Chip>{place.category.label}</Chip> : <Dash />}
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        <IconAction title="Menu" onClick={() => setActive("menu")}>
          <ArrowTopRightOnSquareIcon width="16" height="16" color="#555" />
        </IconAction>
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        <IconAction title="Heures d'ouverture" onClick={() => setActive("hours")}>
          <ArrowTopRightOnSquareIcon width="16" height="16" color="#555" />
        </IconAction>
      </CTableDataCell>

      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          <IconAction title="Modifier" to={`/places/update/${place.id}`}>
            <EditIcon width="16" height="16" fill={colors.secondary} />
          </IconAction>

          {isAdmin && (
            <>
              <IconAction
                title="Traduire"
                to="/place-translations/new"
                state={{ place }}
              >
                <TranslateIcon width="18" height="18" fill="#000" />
              </IconAction>
              <IconAction
                title="Supprimer"
                danger
                onClick={() => setActive("delete")}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <CSpinner size="sm" />
                ) : (
                  <DeleteIcon width="18" height="18" fill={colors.primary} />
                )}
              </IconAction>
            </>
          )}

          <IconAction title="Plus d'actions" onClick={() => setActive("more")}>
            <EllipsisHorizontalIcon width="18" height="18" color="#555" />
          </IconAction>
        </div>
      </CTableDataCell>

      <PlaceRowModals
        place={place}
        active={active}
        setActive={setActive}
        isAdmin={Boolean(isAdmin)}
        onDelete={() => remove(place.id)}
      />
    </CTableRow>
  );
}